import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { StackedBarShape } from '../../../../SharedComponents/StackedBarShape';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { GraphWrapper } from '../../../../SharedComponents/GraphWrapper';
import { MonthAndYearCustomTick } from '../../../../SharedComponents/MonthAndYearCustomTick';
import { SPEND_CATEGORY_COLORS } from '../../../../SharedComponents/Map/Dots/Dots.consts';
import { format } from 'date-fns';
import { formatNumber, formatStackedNumbers } from '../../../../../utils';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import { formatCurrency } from '../../../../../utils/formatCurrency';
import type { AdSpendMode, AdvertisingSpendChartData, PackageTypeInfo } from './AdvertisingSpend.utils';
import { ClientAdProductType } from '@zg-rentals/ts-customer-analytics-client';
import type { CustomerAnalyticsAdspendCategory } from '@zg-rentals/ts-customer-analytics-client';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

type AdvertisingSpendProps = {
  selectedRegionId: number;
  chartData: Array<AdvertisingSpendChartData>;
  allPackageTypesPresent: Array<PackageTypeInfo>;
};

export const AdvertisingSpend = ({ selectedRegionId, chartData, allPackageTypesPresent }: AdvertisingSpendProps) => {
  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioRentLastUpdated);
  const [mode, setMode] = useState<AdSpendMode>('total-spend');

  const isListingSpotlightSelected = selectedFilters.package === ClientAdProductType.LISTING_SPOTLIGHT;

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const currentMonthData = chartData.find((data) => data.name === currentDateFormatted);
  const totalSpend = currentMonthData?.totalSpend || 0;
  const totalProspects = currentMonthData?.totalProspects || 0;
  const avgCostPerProspect = totalProspects > 0 ? totalSpend / totalProspects : 0;

  const graphName = mode === 'cost-per-prospect' ? 'Advertising spend' : 'Advertising spend';
  const title = getGraphTitle({
    graphName,
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  const subtitle =
    mode === 'cost-per-prospect'
      ? `Cost per prospect for your ${selectedFilters.listingAlias !== '' ? 'property' : 'portfolio'}.${lastUpdatedDate ? ` Last updated ${lastUpdatedDate}.` : ''}`
      : `The total spend for your ${selectedFilters.listingAlias !== '' ? 'property' : 'portfolio'}.${lastUpdatedDate ? ` Last updated ${lastUpdatedDate}.` : ''}`;

  const infoCards =
    mode === 'cost-per-prospect'
      ? [
          {
            title: 'Avg. cost per prospect',
            infoText:
              'Average cost per prospect (phone leads, email leads, and web referrals) for your portfolio or property in the selected market, month to date.',
            value: avgCostPerProspect,
            formatter: (value: number | string) => formatCurrency(value as number),
          },
        ]
      : [
          {
            title: 'Total spend',
            infoText: 'Total advertising spend for your portfolio or property in the selected market, month to date.',
            value: totalSpend,
            formatter: (value: number | string) => `$${formatNumber(value as number)}`,
          },
        ];

  return (
    <GraphWrapper
      graphId="advertising-spend"
      title={title}
      subtitle={subtitle}
      infoCards={infoCards}
      loading={isFetchingInitialData}
    >
      {!isListingSpotlightSelected && (
        <Box css={{ marginTop: '-800', marginBottom: '400' }}>
          <ToggleButtonGroup
            aria-label="Ad spend display mode"
            value={mode}
            onValueChange={(value) => setMode(value as AdSpendMode)}
          >
            <ToggleButton value="total-spend">Total spend</ToggleButton>
            <ToggleButton value="cost-per-prospect">Cost per prospect</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData}>
          <defs>
            <pattern
              id="stripe-cost-per-prospect"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#B88DFC" />
              <rect width="3" height="6" fill="#891EFA" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <YAxis
            tickFormatter={(value) =>
              mode === 'cost-per-prospect' ? formatCurrency(value) : formatStackedNumbers(value)
            }
          />
          {mode === 'cost-per-prospect' ? (
            <>
              <Tooltip
                content={
                  <CustomGraphTooltip
                    formatter={(value: number) => formatCurrency(value)}
                    filterPayload={
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (payload: Array<any>) => payload.filter((entry: any) => entry.value !== 0)
                    }
                  />
                }
              />
              <Legend
                wrapperStyle={{ marginBottom: '-32px' }}
                content={
                  <CustomGraphLegend
                    labelTypes={{
                      'Month to date': 'striped',
                    }}
                  />
                }
              />
              <Bar
                dataKey="Cost per prospect"
                stackId="a"
                fill="#891EFA"
                shape={StackedBarShape}
                name="Portfolio"
                isAnimationActive={false}
              />
              <Bar
                dataKey="Cost per prospect MTD"
                stackId="a"
                fill="url(#stripe-cost-per-prospect)"
                shape={StackedBarShape}
                name="Month to date"
                isAnimationActive={false}
              />
            </>
          ) : (
            <>
              <Tooltip
                content={
                  <CustomGraphTooltip
                    formatter={(value: number) => formatCurrency(value)}
                    displayTotal={true}
                    totalKey="totalSpend"
                  />
                }
              />
              <Legend wrapperStyle={{ marginBottom: '-32px' }} content={<CustomGraphLegend />} />
              {allPackageTypesPresent.map(({ packageType, packageTypeString }) => {
                const color =
                  packageType === 'SFH_BOOST'
                    ? '#FF6B35'
                    : SPEND_CATEGORY_COLORS[packageType as CustomerAnalyticsAdspendCategory];
                return (
                  <Bar key={packageType} dataKey={packageTypeString} stackId="a" fill={color} shape={StackedBarShape} />
                );
              })}
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
